import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import type { AlertColor } from "@mui/material/Alert/Alert";
import React from "react";

import type QueryClient from "../../api/query/queryClient";

interface InsuranceArUploadDialogProps {
  queryClient: QueryClient;
  practices: PracticeMessage[];
  selectedPractices: string[];
  pmsSources: SourceMessage[];
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
  onUploaded: () => void;
}

export default function InsuranceArUploadDialog({
  queryClient,
  practices,
  selectedPractices,
  pmsSources,
  updateSnackBar,
  onUploaded,
}: InsuranceArUploadDialogProps) {
  const [open, setOpen] = React.useState(false);
  const [practiceId, setPracticeId] = React.useState("");
  const [source, setSource] = React.useState("");
  const [file, setFile] = React.useState<File | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const practiceMenuItems = React.useMemo(
    () =>
      practices
        .filter((practice) => selectedPractices.includes(practice.wieldyId))
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
        .map((practice) => (
          <MenuItem key={practice.wieldyId} value={practice.wieldyId}>
            {practice.displayName}
          </MenuItem>
        )),
    [practices, selectedPractices],
  );

  const reset = () => {
    setPracticeId("");
    setSource("");
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    if (uploading) return;
    setOpen(false);
    reset();
  };

  const handleSubmit = async () => {
    if (!practiceId || !source || !file) return;
    setUploading(true);
    try {
      const result = await queryClient.uploadInsuranceArReport(
        practiceId,
        source,
        file,
      );
      if (!result.created) {
        updateSnackBar({
          severity: "error",
          message: "This report has already been uploaded for this practice.",
        });
        return;
      }
      const count =
        result.entryCount != null ? ` (${result.entryCount} entries)` : "";
      updateSnackBar({
        severity: "success",
        message: `Insurance A/R report uploaded${count}.`,
      });
      setOpen(false);
      reset();
      onUploaded();
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: intentional browser console output
      console.error("Failed to upload insurance A/R report:", error);
      updateSnackBar({
        severity: "error",
        message: "Failed to upload the insurance A/R report.",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Button variant="contained" onClick={() => setOpen(true)}>
        Upload report
      </Button>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Upload Insurance A/R report</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ marginTop: "8px" }}>
            <FormControl fullWidth size="small">
              <InputLabel id="insurance-ar-upload-practice-label">
                Practice
              </InputLabel>
              <Select
                labelId="insurance-ar-upload-practice-label"
                label="Practice"
                value={practiceId}
                onChange={(e) => setPracticeId(e.target.value)}
              >
                {practiceMenuItems}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel id="insurance-ar-upload-source-label">
                Source
              </InputLabel>
              <Select
                labelId="insurance-ar-upload-source-label"
                label="Source"
                value={source}
                onChange={(e) => setSource(e.target.value)}
              >
                {pmsSources.map((s) => (
                  <MenuItem key={s.id} value={s.name}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button variant="outlined" component="label">
              {file ? file.name : "Choose file"}
              <input
                ref={fileInputRef}
                type="file"
                hidden
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </Button>
            {file ? (
              <Typography sx={{ fontSize: "13px", color: "#475467" }}>
                Selected: {file.name}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={uploading || !practiceId || !source || !file}
          >
            {uploading ? "Uploading…" : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
